import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';

export const Sidebar = ({ className, onClose }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useLanguage();

  const menuItems = [
    { icon: 'fa-solid fa-house', labelKey: 'dashboard', href: '/' },
    { icon: 'fa-solid fa-briefcase', labelKey: 'portfolio', href: '/portfolio' },
    { icon: 'fa-solid fa-diagram-project', labelKey: 'blockchainFlow', href: '/blockchain-flow' },
    { icon: 'fa-solid fa-clock-rotate-left', labelKey: 'transactions', href: '/transactions' },
    { icon: 'fa-solid fa-chart-line', labelKey: 'markets', href: '/markets' },
    { icon: 'fa-solid fa-arrow-right-arrow-left', labelKey: 'exchange', href: '/exchange' },
    { icon: 'fa-solid fa-bell', labelKey: 'alerts', href: '/alerts' },
    { icon: 'fa-solid fa-swatchbook', labelKey: 'designSystem', href: '/design-system' },
  ];

  const bottomItems = [
    { icon: 'fa-solid fa-gear', labelKey: 'settings', href: '/settings' },
    { icon: 'fa-solid fa-circle-question', labelKey: 'help', href: '/help' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <aside 
      className={cn(
        "h-full flex flex-col bg-card border-r border-border",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-[68px]" : "w-60",
        className
      )}
    >
      {/* Logo */}
      <div className="h-14 flex items-center px-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
            <img src="/cryptox-icon.png" alt="CryptoX" className="w-full h-full object-cover" />
          </div>
          {!collapsed && (
            <span className="font-semibold text-foreground truncate">CryptoX</span>
          )}
        </div>
      </div>

      {/* Collapse Toggle - Desktop only */}
      <div className="px-3 py-2 border-b border-border hidden lg:block">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-center"
          onClick={() => setCollapsed(!collapsed)}
        >
          <i className={cn(
            "fa-solid text-xs text-muted-foreground",
            collapsed ? "fa-angles-right" : "fa-angles-left"
          )}></i>
        </Button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 py-2 px-2 space-y-1 overflow-y-auto scrollbar-hide">
        {menuItems.map((item) => {
          const active = isActive(item.href);
          return (
            <Link key={item.labelKey} to={item.href} onClick={onClose}>
              <Button
                variant={active ? "secondary" : "ghost"}
                className={cn(
                  "w-full gap-3 h-10",
                  collapsed ? "justify-center px-0" : "justify-start px-3",
                  active && "bg-primary/10 text-primary font-medium"
                )}
              >
                <i className={cn(item.icon, "text-sm w-4", active && "text-primary")}></i>
                {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
              </Button>
            </Link>
          );
        })}
      </nav>

      {/* Bottom Navigation */}
      <div className="border-t border-border py-2 px-2 space-y-1 shrink-0">
        {bottomItems.map((item) => (
          <Link key={item.labelKey} to={item.href} onClick={onClose}>
            <Button
              variant="ghost"
              className={cn(
                "w-full gap-3 h-10 text-muted-foreground hover:text-foreground",
                collapsed ? "justify-center px-0" : "justify-start px-3"
              )}
            >
              <i className={cn(item.icon, "text-sm w-4")}></i>
              {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
            </Button>
          </Link>
        ))}
      </div>

      {/* User Profile */}
      {!collapsed && (
        <div className="border-t border-border p-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
              <span className="text-secondary-foreground font-medium text-sm">TL</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">Tim Luczak</p>
              <p className="text-xs text-muted-foreground truncate">{t('proTrader')}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
